// THE single resolver for "which backend is this console talking to".
//
// There used to be two: this generic derivation (in backOfficeApi) and a second,
// stale copy inside authClient.js gated on a hardcoded 8-entry BACKEND_DOMAINS
// allowlist. They agreed on tkt.ug/fanpass and disagreed everywhere else:
//
//   backoffice.moments.ug → backOfficeApi: backend.moments.ug | authClient: backend.tkt.ug
//   backoffice.mookh.com  → backOfficeApi: backend.mookh.com  | authClient: backend.tkt.ug
//
// The session cookie lives on the host backOfficeApi authenticated against, so every
// better-auth SDK call (admin.createUser, organization.inviteMember, the sign-in OTP
// itself) went to a DIFFERENT registrable domain with no cookie — better-auth's
// /admin/create-user answers a session-less request with 401 UNAUTHORIZED (a real
// admin who merely lacks the permission gets 403), surfacing in the console as a
// bare "Could not create user". One resolver, one answer, no drift.
//
// Derivation is generic — backoffice.<domain> → backend.<domain> — so a new product
// domain works without an allowlist edit. Local/preview hosts have no backend.<domain>
// sibling, so they fall back to the build-time override.
const isLocalOrPreviewHost = (hostname) =>
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === '0.0.0.0' ||
  /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) || // bare IP
  hostname.endsWith('.local') ||
  hostname.endsWith('.workers.dev');          // CF preview mirror has no backend.<domain>

// "which <sub>.<domain> sibling of the host we're served on" — the ONE place that idiom
// lives. The console derives three of them: backend.<domain> (API), apis.<domain> (the
// ticket-PDF pass service) and ws.<domain> (the WhatsApp socket, which now proxies to the
// API's own port rather than a listener of its own). They were three hand-copied blocks
// sharing one private predicate, which is how deleting that predicate took out two call
// sites nobody had grepped for. `fallback` covers local/preview hosts, where no such
// sibling exists; `ws` swaps http(s) → ws(s).
export const derivedHostUrl = (sub, fallback, { ws = false } = {}) => {
  if (typeof window !== 'undefined' && window.location?.host) {
    const { protocol, host } = window.location;
    const hostname = host.split(':')[0]; // strip any :port
    if (!isLocalOrPreviewHost(hostname)) {
      const parts = hostname.split('.');
      // "<sub>.<domain.tld>" → strip the first label; a bare apex domain is used as-is.
      const domain = parts.length > 2 ? parts.slice(1).join('.') : hostname;
      const scheme = ws ? (protocol === 'https:' ? 'wss:' : 'ws:') : protocol;
      return `${scheme}//${sub}.${domain}`;
    }
  }
  return typeof fallback === 'function' ? fallback() : fallback;
};

export const getBackendUrl = () =>
  derivedHostUrl('backend', () => import.meta.env.VITE_TKT_BACKEND_URL || 'http://127.0.0.1:3001');

// ─── EVERY CROSS-HOST URL THIS CONSOLE BUILDS, IN ONE PLACE ──────────────────────────────────
//
// The header above records what happens otherwise: two resolvers for "which host", disagreeing on
// every domain but two, and a session cookie written to one of them. The same class had already
// put a hardcoded `tkt.ug/e/{slug}` in six places — a 404 on every non-tkt deployment.
//
// So a host is named here or it is not built. `scripts/check-host-literals.mjs` fails the build on
// a product-host literal anywhere else in src/, which is the only thing that keeps this list the
// single copy rather than the first of several.
//
// Two kinds live here, and the difference is the whole point:
//   · DERIVED   — a per-deployment sibling of the console's own host.
//   · FIXED     — one shared host for every tenant, with the evidence for why.

/** The storefront / organiser site. tkt-frontend deploys to NEXT_PUBLIC_CLIENT_URL=front.ticket.ug;
 *  the back office links out to pages that live there (the public admissions view). DERIVED. */
export const getFrontUrl = () =>
  derivedHostUrl('front', () => import.meta.env.VITE_TKT_FRONT_URL || null);

/** Uploaded documents and artwork (KYC files, event posters). DERIVED — each deployment stores its
 *  own uploads. NOTE the two spellings live in prod data: `EventPage.photo` holds
 *  media.ticket.ug URLs while the KYC screens were hardcoding media.tkt.ug. A stored absolute URL
 *  is used as-is; this is only for composing one from a key. */
export const getMediaUrl = () =>
  derivedHostUrl('media', () => import.meta.env.VITE_TKT_MEDIA_URL || 'https://media.tkt.ug');

/** The AI/MCP server this console's operators add to Claude. DERIVED — each brand runs its own,
 *  talking to its own backend, exactly as mcp.<domain> does (ai.fanpass.ke and mcp.fanpass.ke both
 *  resolve; the hardcoded ai.tkt.ug this replaced would have handed a fanpass operator a URL that
 *  authenticates against the wrong backend). Not every brand has one deployed yet — that is a
 *  deployment gap, and pointing at the brand's own address is how it stays visible as one. */
export const getAiUrl = () =>
  derivedHostUrl('ai', () => import.meta.env.VITE_TKT_AI_URL || 'https://ai.tkt.ug');

/** FIXED — the legacy PHP app at ticket.ug that resolves a Meta media id to the actual bytes
 *  (`/replies/mediaget.php`, answering 200 today). Inbound WhatsApp media has only a Meta id, and
 *  Meta's own URL is ephemeral and needs our access token, so no browser can fetch it directly.
 *  tkt-frontend's whatsapp-formatter already points here; there is no per-brand sibling, because
 *  the token this endpoint holds belongs to one WhatsApp number. Deriving it would 404. */
export const getWhatsAppMediaProxyUrl = () =>
  import.meta.env.VITE_TKT_WA_MEDIA_PROXY_URL || 'https://ticket.ug';

/** The accreditation invitation page a guest opens from their card. DERIVED. */
export const getInvitationUrl = () =>
  derivedHostUrl('i', () => import.meta.env.VITE_TKT_INVITATION_URL || 'https://i.tkt.ug');

/** The BUYER-facing storefront an affiliate link points at — the apex, not a sibling. Derivation
 *  cannot produce it (there is no `buy.<domain>`), so it is the console's own registrable domain:
 *  the host with its first label stripped.
 *
 *  IT REUSES `isLocalOrPreviewHost` RATHER THAN RESTATING IT. The first version inlined its own
 *  copy of that predicate and, in copying, dropped the bare-IP case — so on a LAN address like
 *  `192.168.1.20:5173` it saw three labels, stripped the first, and returned `http://168.1.20`.
 *  Affiliate tracking links and the TSPL sample QR are built from this. That is the whole bug this
 *  file exists to end, committed inside the file that ends it: a predicate with two copies has two
 *  behaviours, and the second one is always the one nobody tested. */
export const getConsumerUrl = () => {
  if (typeof window !== 'undefined' && window.location?.host) {
    const { protocol, host } = window.location;
    const hostname = host.split(':')[0];
    const parts = hostname.split('.');
    if (parts.length > 2 && !isLocalOrPreviewHost(hostname)) {
      return `${protocol}//${parts.slice(1).join('.')}`;
    }
  }
  return import.meta.env.VITE_TKT_CONSUMER_URL || 'https://tkt.ug';
};

/** THE HOST IN A PRINTED TICKET'S QR. The gate scanner resolves only the last path segment (the
 *  signature), so any host scans — but a phone camera shows the URL, and on another brand's stock a
 *  foreign host reads as a fake ticket. It is NOT derived: no `go.<domain>` sibling is known to exist
 *  for the other brands, and a derived host that does not resolve is worse than a known one. Default
 *  = the host the box office till prints today (tkt-new-ui printTickets.ts); a deployment with its
 *  own short-link host sets VITE_TKT_VERIFY_URL. */
export const getTicketVerifyUrl = () => import.meta.env.VITE_TKT_VERIFY_URL || 'https://go.tkt.ug';

/** WHERE TKT PRINT (the desktop app, repo tkt-desk) IS DOWNLOADED. One host, not derived — it is
 *  Loft's, not a tenant's. Installer names never change, so these links stay valid across releases. */
export const getTktPrintSiteUrl = () => import.meta.env.VITE_TKT_PRINT_URL || 'https://print.loft.ug';

/**
 * THE GATE SCANNER FOR THIS TENANT — ~/ac_scanner, one app built in a flavor per brand (src/flavors.js there).
 * Its web build answers on scanner.<apex> and takes the tenant from that hostname alone; its Android builds are
 * published to one bucket for every brand (RELEASE.md: scanner-apps/<flavor>-scanner.apk on media.ticket.ug).
 *
 * Keyed by the CONSOLE's apex, which is not always the scanner's: momoticketing.com is the moments backend
 * (same process, measured 2026-10-12), and the Mookh console is on mookh.com while its scanner is party.ug's.
 * `scheme` is the app's deep link, for a device code (ui/ShareDeviceCode).
 */
const SCANNER_FLAVORS = {
  tkt: { name: 'TKT Scanner', scheme: 'tktscanner' },
  mookh: { name: 'Mookh Scanner', scheme: 'mookhscanner' },
  moments: { name: 'Ticket Scanner', scheme: 'momentsscanner' },
  fanpass: { name: 'Fanpass Scanner', scheme: 'fanpassscanner' },
  tix: { name: 'Tickway Scanner', scheme: 'tixscanner' },
  mandela: { name: 'Mandela Scanner', scheme: 'mandelascanner' },
};
const SCANNER_BY_APEX = {
  'tkt.ug': ['tkt', 'tkt.ug'], 'ticket.ug': ['tkt', 'ticket.ug'],
  'party.ug': ['mookh', 'party.ug'], 'mookh.com': ['mookh', 'party.ug'],
  'moments.ug': ['moments', 'moments.ug'], 'momoticketing.com': ['moments', 'moments.ug'],
  'fanpass.ke': ['fanpass', 'fanpass.ke'], 'tix.ug': ['tix', 'tix.ug'], 'mandelastadium.ug': ['mandela', 'mandelastadium.ug'],
};
const scannerApp = (flavor, apex) => ({
  flavor, ...SCANNER_FLAVORS[flavor] || { name: 'Access Control', scheme: 'acscanner' },
  webUrl: `https://scanner.${apex}`,
  apkUrl: `https://media.ticket.ug/scanner-apps/${flavor}-scanner.apk`,
});

/** This tenant's scanner: name, deep-link scheme, web app and Android download. NULL on a host that names no
 *  tenant — local and preview consoles included, unlike the getters above: a device code minted on a local backend
 *  handed to scanner.tkt.ug would send a steward to production with a code it has never seen. */
export const getScannerApp = () => {
  const hostname = typeof window !== 'undefined' ? window.location?.hostname || '' : '';
  const hit = !hostname || isLocalOrPreviewHost(hostname) ? null : SCANNER_BY_APEX[hostname.split('.').slice(1).join('.')];
  return hit ? scannerApp(...hit) : null;
};

/** FIXED — Loft's Access Control family: its own Worker and backend (ac-backend.<apex>), one brand, on loft.ug. */
export const ACCESS_CONTROL_SCANNER = scannerApp('accesscontrol', 'loft.ug');

/** FIXED — the ticket renderer (~/ticketapis): ONE service for every tenant; the owning backend travels in the path
 *  (tkt-backend utils/passUrl.ts). Its GET /stats is the print service's own account of its queue. */
export const getRendererUrl = () => import.meta.env.VITE_TKT_RENDERER_URL || 'https://apis.tkt.ug';
/** Every renderer server running, for the print service panel to show side by side. */
export const RENDERER_URLS = ['https://apis.tkt.ug', 'https://apis.ticket.ug'];
/** Each renderer's Chrome DevTools (that box's Apache → localhost:9222), which pushes its tabs as they change. */
export const RENDERER_DEVTOOLS = { 'https://apis.tkt.ug': 'printstats.loftuganda.tech', 'https://apis.ticket.ug': 'printstats.ticket.ug' };
/** FIXED — that renderer's headless Chrome, its DevTools HTTP interface behind a tunnel: the tabs open right now. */
export const getRendererChromeUrl = () => import.meta.env.VITE_TKT_RENDERER_CHROME_URL || 'https://printstats.loftuganda.tech';
